import React, { useEffect, useState } from 'react';
import { Flame, TrendingUp, Loader2 } from 'lucide-react';
import axios from 'axios';

/**
 * BookTok Feed — a live shelf of trending BookTok titles, refreshed weekly.
 * Data is served by the backend, which caches an LLM-curated list for 7 days.
 * Falls back to a static seed list if the API is unreachable.
 */

const SEED_TRENDING = [
  { title: 'Onyx Storm', author: 'Rebecca Yarros', isbn: '9781649374189', tag: 'Dragons & spice', accent: '#0A0A0A' },
  { title: 'A Fate Inked in Blood', author: 'Danielle L. Jensen', isbn: '9780593599839', tag: 'Norse fantasy', accent: '#1F3541' },
  { title: 'Divine Rivals', author: 'Rebecca Ross', isbn: '9781250857439', tag: 'Rivals to lovers', accent: '#173B2A' },
  { title: 'Fourth Wing', author: 'Rebecca Yarros', isbn: '9781649374042', tag: 'Dragon academy', accent: '#3B1F2B' },
  { title: 'Powerless', author: 'Lauren Roberts', isbn: '9781665954945', tag: 'Enemies to lovers', accent: '#2D1B36' },
  { title: 'Icebreaker', author: 'Hannah Grace', isbn: '9781668026038', tag: 'Hockey romance', accent: '#1F3B4A' },
];

const coverUrl = (isbn) =>
  isbn
    ? `https://covers.openlibrary.org/b/isbn/${isbn}-L.jpg?default=false`
    : null;

const BookTokFeed = () => {
  const [books, setBooks] = useState(SEED_TRENDING);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [failedIsbns, setFailedIsbns] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/booktok/trending`,
          { withCredentials: true }
        );
        if (!cancelled && Array.isArray(data?.books) && data.books.length > 0) {
          setBooks(data.books);
          setFetchedAt(data.fetched_at || null);
          setFailedIsbns(new Set()); // reset when list changes
        }
      } catch (_) {
        // Keep the seed list on error — no toast, this is a background enhancement.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const markFailed = (isbn) => {
    if (!isbn) return;
    setFailedIsbns((prev) => {
      if (prev.has(isbn)) return prev;
      const next = new Set(prev);
      next.add(isbn);
      return next;
    });
  };

  // Only render books that (a) have an ISBN and (b) whose cover hasn't failed to load.
  const visibleBooks = books
    .filter((b) => (b.cover_url || b.isbn) && !failedIsbns.has(b.isbn || b.title))
    .slice(0, 6);

  const freshnessLabel = fetchedAt
    ? `Updated ${new Date(fetchedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
    : 'Curated weekly';

  // If we finished loading and every cover failed, hide the section entirely
  // so the dashboard doesn't render an empty shelf.
  if (!loading && visibleBooks.length === 0) return null;

  return (
    <section
      data-testid="booktok-feed"
      className="mt-16 max-w-6xl mx-auto"
    >
      <div className="flex items-center justify-between mb-6 px-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6B7A4F] to-[#434E30] flex items-center justify-center shadow-md">
            <Flame className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#2D2A26]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Trending on BookTok
            </h2>
            <p className="text-sm text-[#5C5955] flex items-center gap-1">
              {loading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading this week's picks...
                </>
              ) : (
                <>
                  <TrendingUp className="w-3.5 h-3.5" />
                  What everyone's reading this week
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8 px-2 max-w-3xl mx-auto">
        {visibleBooks.map((book) => (
          <a
            key={`${book.title}-${book.author}`}
            href={`https://openlibrary.org/search?q=${encodeURIComponent(book.title + ' ' + book.author)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`booktok-item-${book.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className="group flex flex-col items-center text-center cursor-pointer"
            aria-label={`${book.title} by ${book.author} — opens Open Library`}
          >
            {/* Book cover with shelf shadow */}
            <div className="relative w-full aspect-[2/3] rounded-md overflow-hidden shadow-[4px_6px_14px_rgba(0,0,0,0.25)] transition-transform duration-300 group-hover:-translate-y-2 group-hover:shadow-[6px_10px_20px_rgba(0,0,0,0.35)]">
              <img
                src={book.cover_url || coverUrl(book.isbn)}
                alt={`${book.title} cover`}
                loading="lazy"
                className="w-full h-full object-cover"
                onError={() => markFailed(book.isbn || book.title)}
              />
              {/* Sparkle bookmark hover */}
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="inline-block bg-[#6B7A4F] text-white text-[9px] font-bold px-2 py-1 rounded-full shadow">
                  #BookTok
                </span>
              </div>
            </div>
            {/* Title & tag */}
            <h3 className="mt-3 text-sm font-semibold text-[#2D2A26] leading-tight line-clamp-2">
              {book.title}
            </h3>
            <p className="text-xs text-[#5C5955] mt-0.5">{book.author}</p>
            <span
              className="mt-2 inline-block text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-full"
              style={{
                background: '#E2E6D5',
                color: '#6B7A4F',
              }}
            >
              {book.tag}
            </span>
          </a>
        ))}
      </div>

      <p className="text-center text-xs text-[#5C5955] mt-6 opacity-70">
        {freshnessLabel} · Click any cover to explore on Open Library
      </p>
    </section>
  );
};

export default BookTokFeed;
